import humanFileSize from '@/util/humanFileSize';
import type { ExecutionStatus, ReportType } from './types';

/**
 * Normalize execution status from GraphQL (uppercase) to internal format (lowercase).
 * Type-safe mapping that handles both formats.
 */
export function normalizeStatus(status: ExecutionStatus): 'success' | 'failed' | 'running' {
  const normalized = status.toLowerCase();
  if (normalized === 'success' || normalized === 'failed' || normalized === 'running') {
    return normalized;
  }
  return 'running';
}

/**
 * Normalize report type from GraphQL (uppercase) to internal format (lowercase).
 */
export function normalizeReportType(reportType: ReportType): 'weekly' | 'monthly' | 'daily_csv' {
  const normalized = reportType.toLowerCase();
  if (normalized === 'weekly' || normalized === 'monthly' || normalized === 'daily_csv') {
    return normalized;
  }
  return 'weekly';
}

/**
 * Format duration in milliseconds to human-readable string.
 */
export function formatDuration(ms?: number): string {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

/**
 * Format bytes to human-readable string using existing utility.
 * Re-export for convenience.
 */
export function formatBytes(bytes?: number): string {
  if (!bytes) return '-';
  return humanFileSize(bytes, true, 1);
}

/**
 * Get display label for report type.
 */
export function getReportTypeLabel(reportType: ReportType): string {
  const normalized = normalizeReportType(reportType);
  switch (normalized) {
    case 'weekly':
      return 'Weekly';
    case 'monthly':
      return 'Monthly';
    case 'daily_csv':
      return 'Daily CSV';
    default:
      return reportType;
  }
}

/** Time range options for execution history filter. */
export const TIME_RANGE_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 60, label: 'Last 60 days' },
  { value: 90, label: 'Last 90 days' },
] as const;

/** Page size for execution history. */
export const EXECUTION_PAGE_SIZE = 25;

/** Polling interval for running executions (ms). */
export const EXECUTION_POLL_INTERVAL = 3000;
